import React from 'react';
import { Message } from '../types';

// 工具名称 → 中文动作描述 + 危险等级
const TOOL_META: Record<string, { action: string; icon: string; level: 'warning' | 'danger' }> = {
    write_local_file:       { action: '创建/写入文件',   icon: '📝', level: 'warning' },
    create_folder_and_move: { action: '新建文件夹并移动文件', icon: '📁', level: 'warning' },
    rename_local_file:      { action: '重命名文件',     icon: '✏️', level: 'warning' },
    delete_local_file:      { action: '删除文件',       icon: '🗑️', level: 'danger'  },
    manage_workspace_files: { action: '批量文件管理',   icon: '⚙️', level: 'danger'  },
};

interface ToolRequestCardProps {
    toolRequest: NonNullable<Message['toolRequest']>;
    onApprove: () => void;
    onReject: () => void;
}

const ToolRequestCard: React.FC<ToolRequestCardProps> = ({ toolRequest, onApprove, onReject }) => {
    const meta = TOOL_META[toolRequest.tool] || { action: toolRequest.tool, icon: '⚠️', level: 'warning' };
    const isDanger = meta.level === 'danger';
    const isPending = toolRequest.status === 'pending';
    const isApproved = toolRequest.status === 'approved';
    const isRejected = toolRequest.status === 'rejected';

    // 提取展示用的参数摘要
    const targetPath = toolRequest.args.path || toolRequest.args.old_path || toolRequest.args.folder_name || '未知路径';
    const description = toolRequest.args.description || toolRequest.args.new_name || '';

    // 根据状态显示不同的样式
    const borderColor = isPending
        ? (isDanger ? 'border-red-400' : 'border-amber-400')
        : isApproved
            ? 'border-green-400'
            : 'border-gray-300';

    const bgColor = isPending
        ? (isDanger ? 'bg-red-50' : 'bg-amber-50')
        : isApproved
            ? 'bg-green-50'
            : 'bg-gray-50';

    return (
        <div className={`mt-3 rounded-xl border-2 ${borderColor} ${bgColor} p-4 max-w-md shadow-sm transition-all duration-300`}>
            {/* 顶部标签 */}
            <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">{meta.icon}</span>
                <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                    isPending
                        ? (isDanger ? 'bg-red-200 text-red-800' : 'bg-amber-200 text-amber-800')
                        : isApproved
                            ? 'bg-green-200 text-green-800'
                            : 'bg-gray-200 text-gray-600'
                }`}>
                    {isPending ? '权限请求' : isApproved ? '已授权' : '已拒绝'}
                </span>
            </div>

            {/* 操作描述 */}
            <div className="mb-3 text-sm text-gray-700">
                <div className="font-semibold mb-1">{meta.icon} AI 请求执行: {meta.action}</div>
                {/* 动态解释（AI 自动生成） */}
                {toolRequest.args.user_explanation ? (
                    <div className="bg-white/70 rounded-lg px-3 py-2 mt-1 text-sm text-gray-800 leading-relaxed border border-gray-100">
                        💬 {toolRequest.args.user_explanation}
                    </div>
                ) : (
                    <div className="font-mono text-xs bg-white/60 rounded px-2 py-1 break-all">
                        📍 {targetPath}
                    </div>
                )}
                {/* 目标路径（始终展示，让用户能确认具体位置） */}
                {toolRequest.args.user_explanation && targetPath && (
                    <div className="font-mono text-xs text-gray-400 mt-1 break-all">
                        📍 目标: {targetPath}
                    </div>
                )}
            </div>

            {/* 按钮区 */}
            {isPending && (
                <div className="flex gap-2">
                    <button
                        onClick={onApprove}
                        className="flex-1 py-2 px-3 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 active:scale-95 transition-all shadow-sm cursor-pointer"
                    >
                        ✅ 授权执行
                    </button>
                    <button
                        onClick={onReject}
                        className="flex-1 py-2 px-3 rounded-lg text-sm font-semibold text-gray-600 bg-white border border-gray-300 hover:bg-gray-100 active:scale-95 transition-all cursor-pointer"
                    >
                        🚫 拒绝
                    </button>
                </div>
            )}

            {/* 已决状态 */}
            {isApproved && (
                <div className="text-xs text-green-700 font-medium">✅ 已授权，正在执行中...</div>
            )}
            {isRejected && (
                <div className="text-xs text-gray-500 font-medium">🚫 操作已被拒绝，AI 将调整方案。</div>
            )}
        </div>
    );
};

export default ToolRequestCard;
